/*
 * The shared dialog, the destructive confirmation, and the detail drawer.
 *
 * Focus is the whole job here. A dialog that opens without moving focus into itself is a dialog a
 * keyboard user is still standing outside of, and one that does not give focus back on close drops
 * them at the top of the document. So: focus moves in on open, is trapped while open, Escape closes,
 * and the element that opened it gets focus back.
 *
 * `ConfirmDialog` exists because "คุณแน่ใจหรือไม่?" is not a confirmation — it asks whether you meant
 * to press the button you just pressed, which you did. The required shape is a *consequence*: what
 * will happen, to what, and whether it can be undone. `consequences` is a required prop for that
 * reason, and destructive confirmations additionally require the name typed back.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { Alert } from './Alert';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function useFocusTrap(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  /*
   * `onClose` is almost always an inline arrow, so its identity changes on every render of the
   * screen holding the drawer. Depending on it re-ran this effect on every keystroke, which moved
   * focus back to the first focusable element — typing a second character into any field inside a
   * drawer was impossible. The handler lives in a ref so the trap is installed once per open.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); return; }
      if (e.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const edge = e.shiftKey ? items[0] : items[items.length - 1];
      if (document.activeElement === edge) {
        e.preventDefault();
        (e.shiftKey ? items[items.length - 1] : items[0]).focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      restoreTo.current?.focus?.();
    };
  }, [open]);

  return ref;
}

export function Dialog({ open, onClose, title, description, children, footer, width = '520px' }: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const id = useId();
  const ref = useFocusTrap(open, onClose);
  if (!open) return null;

  return (
    <div className="jt-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={ref}
        className="jt-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-desc` : undefined}
        style={{ maxWidth: width }}
        tabIndex={-1}
      >
        <h2 className="jt-dialog-title" id={`${id}-title`}>{title}</h2>
        {description && <div className="jt-dialog-desc" id={`${id}-desc`}>{description}</div>}
        {children && <div className="jt-dialog-body">{children}</div>}
        <div className="jt-dialog-foot">{footer}</div>
      </div>
    </div>
  );
}

/**
 * A confirmation that states its consequences.
 *
 * `confirmPhrase` turns the dialog into a typed confirmation — reserved for the actions that cannot
 * be undone (revoking a token in use, disconnecting a provider mid-sale, a refund). Where it is
 * omitted the dialog is still a consequence panel, just one you can accept with a click.
 */
export function ConfirmDialog({
  open, onClose, onConfirm, title, consequences, confirmLabel, confirmPhrase, tone = 'danger', busy = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** What will happen, to what, and whether it can be undone. One line each. */
  consequences: readonly string[];
  confirmLabel: string;
  /** When set, the action stays disabled until this exact text is typed back. */
  confirmPhrase?: string;
  tone?: 'danger' | 'warning';
  busy?: boolean;
}) {
  const [typed, setTyped] = useState('');
  const id = useId();
  useEffect(() => { if (!open) setTyped(''); }, [open]);

  const ready = !confirmPhrase || typed.trim() === confirmPhrase;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>ยกเลิก</Button>
          <Button
            variant={tone === 'danger' ? 'destructive' : 'primary'}
            disabled={!ready || busy}
            onClick={onConfirm}
          >
            {busy ? 'กำลังดำเนินการ…' : confirmLabel}
          </Button>
        </>
      }
    >
      <Alert tone={tone} title="สิ่งที่จะเกิดขึ้น">
        <ul className="jt-consequences">
          {consequences.map((line) => <li key={line}>{line}</li>)}
        </ul>
      </Alert>
      {confirmPhrase && (
        <div className="jt-field" style={{ marginTop: 'var(--jt-space-4)' }}>
          <label className="jt-field-label" htmlFor={`${id}-phrase`}>
            พิมพ์ <span className="jt-mono">{confirmPhrase}</span> เพื่อยืนยัน
          </label>
          <input
            id={`${id}-phrase`}
            className="jt-input jt-mono"
            value={typed}
            autoComplete="off"
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
      )}
    </Dialog>
  );
}

/**
 * The detail drawer — the row you opened, beside the list you opened it from.
 *
 * `dirty` is what makes this more than a panel. The drawers in the v3 contract are editors, and the
 * ways out of one are many and mostly accidental: Escape, the scrim, the ✕, the row behind it. The
 * state contract requires that accidental navigation not silently discard work, so while `dirty` is
 * set every one of those routes lands on the same question instead of closing, and the answer that
 * loses the edit is never the default.
 *
 * The header says it too. A drawer that has unsaved changes and looks identical to one that does
 * not is how the work gets lost before anyone reaches for Escape.
 */
export function Drawer({ open, onClose, title, children, footer, dirty = false, discardLabel = 'ทิ้งการแก้ไข' }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Set while the drawer holds edits that are not saved. Guards every route out. */
  dirty?: boolean;
  discardLabel?: string;
}) {
  const id = useId();
  const [confirming, setConfirming] = useState(false);

  useEffect(() => { if (!open) setConfirming(false); }, [open]);

  // Every exit route funnels through here, so the guard cannot be walked around by using a
  // different one. Escape reaches it via the trap; the scrim and the ✕ call it directly.
  const requestClose = useCallback(() => {
    if (dirty) { setConfirming(true); return; }
    onClose();
  }, [dirty, onClose]);

  const ref = useFocusTrap(open, requestClose);
  if (!open) return null;

  return (
    <div className="jt-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <div ref={ref} className="jt-drawer" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} tabIndex={-1}>
        <div className="jt-drawer-head">
          <div>
            <h2 className="jt-dialog-title" id={`${id}-title`}>{title}</h2>
            {dirty && <p className="jt-drawer-dirty">● ยังไม่บันทึก</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={requestClose} aria-label="ปิด">✕</Button>
        </div>
        {confirming && (
          <Alert tone="warning" title="ปิดโดยยังไม่บันทึก?">
            <p className="jt-dialog-desc">การแก้ไขในแผงนี้จะหายไป และค่าเดิมจะยังอยู่เหมือนก่อนเปิด</p>
            <div className="jt-drawer-guard">
              <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>แก้ไขต่อ</Button>
              <Button size="sm" variant="destructive" onClick={() => { setConfirming(false); onClose(); }}>
                {discardLabel}
              </Button>
            </div>
          </Alert>
        )}
        <div className="jt-drawer-body">{children}</div>
        {footer && <div className="jt-dialog-foot">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * A dialog that is a form — create a wristband, grant credit, add a payment account.
 *
 * It exists so the save path is written once. Submitting is a real form submit, so Enter in a field
 * works without anyone wiring a key handler; `busy` disables the action rather than the fields, so
 * a slow save never blanks what was typed; and `error` renders above the fields and **leaves every
 * value where it was**, because the state contract is explicit that a failed save preserves input.
 * The dialog closing is the caller's decision, made after the save actually succeeded.
 */
export function FormDialog({
  open, onClose, onSubmit, title, description, children, submitLabel,
  busy = false, error = null, width, submitDisabled = false,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  submitLabel: string;
  busy?: boolean;
  /** A failed save. Shown above the fields; the fields keep their values. */
  error?: string | null;
  width?: string;
  /**
   * Set where `onSubmit` would refuse anyway.
   *
   * Several callers guard inside `onSubmit` and return early on an incomplete form, which leaves a
   * button that looks ready and does nothing when pressed — the exact failure TICK-142 is about,
   * one layer down. Saying so on the button is the difference between "not yet" and "broken".
   */
  submitDisabled?: boolean;
}) {
  const formId = `${useId()}-form`;
  return (
    <Dialog
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      description={description}
      width={width}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>ยกเลิก</Button>
          <Button variant="primary" type="submit" form={formId} disabled={busy || submitDisabled}>
            {busy ? 'กำลังบันทึก…' : submitLabel}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => { e.preventDefault(); if (!busy) onSubmit(); }}
      >
        {error && <Alert tone="danger" title="บันทึกไม่สำเร็จ">{error}</Alert>}
        <div className="jt-form-fields">{children}</div>
      </form>
    </Dialog>
  );
}
